/**
 * AutoTranslate
 *
 * Wraps any subtree and, when the active language is Spanish, walks the
 * rendered DOM, sends the unique English text nodes to /api/translate/batch,
 * and swaps them in-place with their Spanish translations. Caches each
 * translation per session in `sessionStorage` so subsequent navigations to
 * the same page render in Spanish instantly without a network call.
 *
 * This avoids having to refactor every static page (Privacy, ToS, Safety,
 * ForOrganizations, LandingPage) into hundreds of `t('key')` calls — the
 * tradeoff is that the first ES paint shows English for ~1-3s while the
 * batch request returns. A `data-translating` attribute is exposed for
 * pages that want to show a subtle loader.
 *
 * Usage:
 *   <AutoTranslate>
 *     <h1>Privacy Policy</h1>
 *     <p>We don't sell your data.</p>
 *   </AutoTranslate>
 *
 * Implementation notes:
 *  - Forces a remount on language toggle via React `key`, so when the user
 *    switches ES → EN the original English JSX text returns naturally
 *    (no need to track originals on each text node).
 *  - Only translates text nodes that contain at least one letter (skips
 *    pure punctuation, numbers, emoji-only nodes, dates).
 *  - Skips text inside <code>, <pre>, <script>, <style>, or anything with
 *    `data-no-translate` to preserve technical content / brand names.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';

const SKIP_TAGS = new Set(['CODE', 'PRE', 'SCRIPT', 'STYLE', 'NOSCRIPT']);
const HAS_LETTER = /[A-Za-z\u00C0-\u017F]/;

/** djb2 hash → short stable string for storage keys. */
const hash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
};
const cacheKey = (text, lang) => `cca_t_${lang}_${hash(text)}`;

/**
 * Shared persistent cache that survives across sessions.
 *
 * Keeping translations in `localStorage` (rather than the previous
 * `sessionStorage`) means that on a user's *second* visit to any
 * static page, every previously-seen English string renders in
 * Spanish on the very first paint — no flicker, no network round-trip.
 *
 * We still try/catch every read & write because some browsers (private
 * mode, locked-down enterprise builds) deny storage access.
 */
const cacheGet = (text, lang) => {
  try { return localStorage.getItem(cacheKey(text, lang)); } catch { return null; }
};
const cacheSet = (text, lang, value) => {
  try { localStorage.setItem(cacheKey(text, lang), value); } catch { /* private mode / quota — ignore */ }
};

const collectTextNodes = (root) => {
  const out = [];
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        // Skip whitespace-only / no-letter / inside skip tag / no-translate flagged
        const v = node.nodeValue;
        if (!v || !v.trim() || !HAS_LETTER.test(v)) return NodeFilter.FILTER_REJECT;
        let p = node.parentElement;
        while (p) {
          if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
          if (p.dataset && p.dataset.noTranslate !== undefined) return NodeFilter.FILTER_REJECT;
          p = p.parentElement;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
};

const InnerAutoTranslate = ({ children }) => {
  const ref = useRef(null);
  const { isSpanish } = useLanguage();
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!isSpanish || !ref.current) return;
    let cancelled = false;
    setTranslating(true);

    let networkStartedAt = null;
    const MIN_VISIBLE_MS = 600;
    const finish = () => {
      // Only enforce the min-visible hold when we actually went to the
      // network — fully-cached visits should feel instant.
      if (networkStartedAt) {
        const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - networkStartedAt));
        setTimeout(() => { if (!cancelled) setTranslating(false); }, remaining);
      } else if (!cancelled) {
        setTranslating(false);
      }
    };

    // Defer one tick so children have committed to the DOM.
    const id = setTimeout(async () => {
      if (cancelled || !ref.current) return;
      const nodes = collectTextNodes(ref.current);
      if (nodes.length === 0) {
        finish();
        return;
      }

      const lang = 'es';
      const queue = [];
      const indexByText = new Map();

      // First pass: apply cached translations + collect uncached strings.
      nodes.forEach((node, i) => {
        const original = node.nodeValue;
        const trimmed = original.trim();
        const lead = original.match(/^\s*/)[0];
        const tail = original.match(/\s*$/)[0];
        const cached = cacheGet(trimmed, lang);
        if (cached) {
          node.nodeValue = `${lead}${cached}${tail}`;
        } else {
          if (!indexByText.has(trimmed)) {
            indexByText.set(trimmed, []);
            queue.push(trimmed);
          }
          indexByText.get(trimmed).push({ node, lead, tail });
        }
      });

      if (queue.length === 0) {
        finish();
        return;
      }

      networkStartedAt = Date.now();
      // Send in chunks of 40 to keep each LLM call lightweight.
      const CHUNK = 40;
      for (let i = 0; i < queue.length; i += CHUNK) {
        if (cancelled) break;
        const chunk = queue.slice(i, i + CHUNK);
        const { data } = await apiFetch('/api/translate/batch', {
          method: 'POST',
          body: { texts: chunk, source_lang: 'en', target_lang: 'es' },
        });
        if (cancelled) break;
        const translations = (data && data.translations) || {};
        chunk.forEach((trimmed) => {
          const translated = translations[trimmed];
          if (!translated || translated === trimmed) return;
          cacheSet(trimmed, lang, translated);
          const targets = indexByText.get(trimmed) || [];
          targets.forEach(({ node, lead, tail }) => {
            // Only swap if the live text still matches what we asked for —
            // guards against React having re-rendered the subtree.
            if (node.nodeValue && node.nodeValue.trim() === trimmed) {
              node.nodeValue = `${lead}${translated}${tail}`;
            }
          });
        });
      }
      finish();
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [isSpanish]);

  return (
    <div ref={ref} data-translating={translating ? 'true' : undefined}>
      {translating && (
        <div
          aria-live="polite"
          className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-[#00E5CC]/40 bg-card/95 px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur"
          data-testid="autotranslate-indicator"
        >
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#00E5CC]" />
          Traduciendo…
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * Top-level wrapper. Forces remount of the inner instance on language
 * toggle so the original English JSX is restored when toggling back.
 */
export const AutoTranslate = ({ children }) => {
  const { isSpanish } = useLanguage();
  return (
    <InnerAutoTranslate key={isSpanish ? 'es' : 'en'}>{children}</InnerAutoTranslate>
  );
};

export default AutoTranslate;
